import type { ReactNode } from 'react';
import { CalendarCheck, ClipboardCheck, PhoneOff, Voicemail, StickyNote, Mail, Check, Info, XCircle } from 'lucide-react';

// Die Handy-Ansicht des laufenden Anrufs — ersetzt auf schmalen Geräten (< 768 px) das Live-Skript.
//
// Warum kein Skript: am Handy wird nicht vorgelesen, sondern TELEFONIERT — die Wähl-App liegt vorn,
// die Leitkraft-App im Hintergrund. Wer zurückwechselt, will in einer Sekunde eines von vier Dingen
// tun: Termin buchen, tracken, „nicht erreicht" melden, notieren. Das sind hier die großen Knöpfe,
// mit dem Daumen im Vorbeigehen treffbar (≥ 4.5 rem hoch), nicht ein Text, den man erst wegscrollen muss.
//
// Reine Anzeige: alles, was gespeichert oder umgeschaltet wird, bleibt im CallCockpit (`save()` ist
// der einzige Schreibweg). Das Bauteil bekommt nur Rückrufe.

export interface CallPadMobilProps {
  elapsed: string;
  name: string;
  company: string;
  phone: string | null;
  isRecording: boolean;
  saving: boolean;
  hatNotiz: boolean;
  mailMoeglich: boolean;
  mailVermerkt: boolean;
  /** Das gewählte Ergebnis, falls schon eines steht — als Hinweis, nicht als Knopf. */
  outcome: string | null;
  onTermin: () => void;
  onTracken: () => void;
  onNichtErreicht: () => void;
  onMailbox: () => void;
  onNotiz: () => void;
  onMail: () => void;
  onInfo: () => void;
  onBeenden: () => void;
}

function Gross({ icon, label, onClick, disabled, variante = 'neutral', badge }: {
  icon: ReactNode; label: string; onClick: () => void; disabled?: boolean;
  variante?: 'haupt' | 'neutral'; badge?: boolean;
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`relative flex flex-col items-center justify-center gap-1.5 rounded-2xl min-h-[4.5rem] px-3 py-3 text-base font-semibold
        active:scale-[0.98] transition-transform disabled:opacity-40 ${
        variante === 'haupt'
          ? 'bg-accent border border-accent'
          : 'bg-bg-elevated border-2 border-stark text-text-main'}`}
    >
      {icon}
      <span className="leading-tight text-center">{label}</span>
      {badge && <span className="absolute top-2.5 right-3 h-2.5 w-2.5 rounded-full bg-accent" />}
    </button>
  );
}

export default function CallPadMobil(p: CallPadMobilProps) {
  return (
    <div className="absolute inset-0 overflow-y-auto px-4 pt-4 pb-[max(1rem,env(safe-area-inset-bottom))] flex flex-col gap-3">
      <div className="text-center">
        <div className="font-mono tabular-nums text-5xl font-bold text-accent leading-none">{p.elapsed}</div>
        {p.isRecording && (
          <div className="mt-1.5 inline-flex items-center gap-1.5 text-xs font-semibold text-red-500">
            <span className="h-2 w-2 rounded-full bg-red-500 animate-pulse" /> Aufnahme läuft
          </div>
        )}
        <div className="mt-2 text-sm text-text-muted truncate">{p.name}{p.company ? ` · ${p.company}` : ''}</div>
        {p.outcome && (
          <div className="mt-1 text-xs text-text-muted">Gewählt: <span className="text-text-main font-medium">{p.outcome}</span></div>
        )}
      </div>

      <Gross variante="haupt" icon={<CalendarCheck size={28} />} label="Termin buchen" onClick={p.onTermin} />
      <Gross variante="haupt" icon={<ClipboardCheck size={28} />} label="Call tracken" onClick={p.onTracken} disabled={p.saving} />

      <div className="grid grid-cols-2 gap-3">
        <Gross icon={<PhoneOff size={24} />} label="Nicht erreicht" onClick={p.onNichtErreicht} disabled={p.saving} />
        <Gross icon={<Voicemail size={24} />} label="Mailbox" onClick={p.onMailbox} disabled={p.saving} />
        <Gross icon={<StickyNote size={24} />} label="Notiz" onClick={p.onNotiz} badge={p.hatNotiz} />
        <Gross icon={p.mailVermerkt ? <Check size={24} /> : <Mail size={24} />} label={p.mailVermerkt ? 'Mail raus' : 'Mail'}
               onClick={p.onMail} disabled={!p.mailMoeglich} />
        <Gross icon={<Info size={24} />} label="Info" onClick={p.onInfo} />
        <Gross icon={<XCircle size={24} />} label="Beenden" onClick={p.onBeenden} />
      </div>
    </div>
  );
}
